import { useState, useEffect, useMemo, useCallback, useRef } from 'preact/hooks';
import { navigate } from '../lib/navigate';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { useThreadEvents } from '../hooks/useThreadEvents';
import { LiveChatPanel } from '../components/LiveChatPanel';
import { LiveReactionLayer } from '../components/LiveReactionLayer';
import { ThreadContextCard } from '../components/ThreadContextCard';
import { ThreadWelcomeCard } from '../components/ThreadWelcomeCard';
import { ContributeSheet } from '../components/ContributeSheet';
import { LiveDigestBar } from '../components/LiveDigestBar';
import { ThreadedContributionList } from '../components/ThreadedContributionList';
import { PresenceBar } from '../components/PresenceBar';
import { LiveToast } from '../components/LiveToast';
import { InstallBanner } from '../components/InstallBanner';
import { PushOptIn } from '../components/PushOptIn';
import { StaleCacheBanner } from '../components/StaleCacheBanner';
import { Spinner } from '../components/Spinner';
import { AIWorkingIndicator } from '../components/AIWorkingIndicator';
import { ChorusHeader, ChorusEmpty, IconPlus } from '../components/chorus';
import { addToQueue, clearFailedForThread, getFailed, setArcShapeForThread } from '../lib/offlineQueue';
import { QUEUE_FAILURE_EVENT, getFailedQueueCount, syncPendingContributions } from '../lib/backgroundSync';
import { getDraft, deleteDraft, deleteExpiredDrafts } from '../lib/videoDraft';
import { ApiError, apiFetch, apiFetchRaw, guestApi, templateApi, uploadPhoto, uploadMedia, uploadMediaWithProgress } from '../api/client';
import { CatchMeUpCard, hasCatchUp } from '../components/CatchMeUpCard';
import type { Thread, Contribution, PromptResponse, StoryBrief } from '../api/types';
import type { PromptMeta } from '../components/UnifiedComposer';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import { RolePickerSheet } from '../components/RolePickerSheet';
import { ArcPickerSheet } from '../components/ArcPickerSheet';
import { TurnOrderBadge } from '../components/TurnOrderBadge';
import {
  isArcSelectionRequired,
  isRoleAlreadyTaken,
  isRoleSelectionRequired,
  isStaleTemplateState,
} from '../api/templateValidationErrors';
import styles from './ContributePage.module.css';

export type UnifiedSubmitEntry = {
  content: string;
  visibility: 'public' | 'private';
  promptMeta?: PromptMeta;
  mediaFile?: Blob | File;
  mediaType?: 'photo' | 'audio' | 'video';
  mediaDuration?: number;
  mediaContentType?: string;
  mediaFilename?: string;
  mediaThumbnailFile?: Blob | File;
  mediaThumbnailFilename?: string;
  mediaWidth?: number;
  mediaHeight?: number;
  waveform?: number[];
  coverFrameTimeMs?: number;
  coverSelectionMode?: 'auto' | 'manual';
  audioTrackDegraded?: boolean;
};

export type PromptFlowNext =
  | { kind: 'advance'; toIndex: number }
  | { kind: 'coverageComplete' }
  | { kind: 'phaseQuotaReached' }
  | { kind: 'storyCompleted' }
  | { kind: 'ordinary' };

export type SubmitResult = {
  delivery: 'posted' | 'queued' | 'partial' | 'failed' | 'deferred';
  createdIndices: number[];
  queuedIndices: number[];
  failedIndices: number[];
  createdCount: number;
  next: PromptFlowNext | null;
};

export type PromptFlowState = 'unanswered' | 'queued' | 'answered';

type FailedSubmission = {
  entries: UnifiedSubmitEntry[];
  buildsOnContributionId?: string;
};

interface ContributePageProps {
  threadId?: string;
  path?: string;
}

function buildContributionMetadata(entry: UnifiedSubmitEntry): Record<string, unknown> | undefined {
  const metadata: Record<string, unknown> = {};

  if (entry.promptMeta) {
    metadata.promptIndex = entry.promptMeta.promptIndex;
    metadata.promptText = entry.promptMeta.promptText;
  }

  if (entry.waveform && entry.waveform.length > 0) {
    metadata.waveform = entry.waveform;
  }

  if (entry.coverFrameTimeMs != null) {
    metadata.coverFrameTimeMs = entry.coverFrameTimeMs;
  }

  if (entry.coverSelectionMode) {
    metadata.coverSelectionMode = entry.coverSelectionMode;
  }

  if (entry.audioTrackDegraded != null) {
    metadata.audioTrackDegraded = entry.audioTrackDegraded;
  }

  return Object.keys(metadata).length > 0 ? metadata : undefined;
}

function resolveUploadContentType(
  preferredType: string | undefined,
  file: Blob | File,
  fallbackType: string,
): string {
  const fileType = file.type && file.type.length > 0 ? file.type : undefined;
  return preferredType ?? fileType ?? fallbackType;
}

export function ContributePage({ threadId }: ContributePageProps) {
  const [thread, setThread] = useState<Thread | null>(null);
  const [prompts, setPrompts] = useState<PromptResponse | null>(null);
  const [storyBrief, setStoryBrief] = useState<StoryBrief | null>(null);
  // Tracked separately from the value because `null` otherwise means three
  // different things — not fetched yet, fetch failed, no brief — and the page
  // needs to tell "still loading" from "settled with nothing". The page-wide
  // `loading` flag cannot stand in: it goes false before the brief request even
  // starts.
  const [briefStatus, setBriefStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // Monotonic load sequence, stamped before the first await in loadThreadData.
  // Only the newest load may write briefStatus/storyBrief — see the reset effect
  // below for why a late response from the previous story is not merely stale
  // but actively harmful.
  const briefLoadRef = useRef(0);
  const [catchUpDismissed, setCatchUpDismissed] = useState(false);
  const [optimisticCreatedIndices, setOptimisticCreatedIndices] = useState<Set<number>>(() => new Set());
  const [queuedPromptIndices, setQueuedPromptIndices] = useState<Set<number>>(() => new Set());
  const [optimisticMyContributionCount, setOptimisticMyContributionCount] = useState<number | null>(null);
  // Survives the render-complete toast being dismissed: the reel doesn't stop
  // existing because someone waved away the notice about it, and the success
  // panel may offer it long after.
  const [renderedHighlightToken, setRenderedHighlightToken] = useState<string | null>(null);
  const [promptLifecycleLocked, setPromptLifecycleLocked] = useState(false);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [hasContributed, setHasContributed] = useState(false);
  // Distinct from hasContributed, which is hydrated from the server below. The
  // post-contribution banners are keyed to "you just posted a take", not "you
  // have ever posted one" — PushOptIn's dismissal in particular is session-local
  // (PushOptIn.tsx:8), so gating it on the hydrated flag would re-prompt a
  // returning contributor on every single page load.
  const [contributedThisSession, setContributedThisSession] = useState(false);
  const [buildOnTarget, setBuildOnTarget] = useState<Contribution | null>(null);
  const [submittingGlobal, setSubmittingGlobal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [failedSubmission, setFailedSubmission] = useState<FailedSubmission | null>(null);
  // Template-gate picker state (Plot Armor role / Mood Ring arc shape).
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  const [rolePickerMode, setRolePickerMode] = useState<'proactive' | 'recovery'>('proactive');
  const [arcPickerOpen, setArcPickerOpen] = useState(false);
  const [arcPickerMode, setArcPickerMode] = useState<'proactive' | 'recovery'>('proactive');
  // Separate error state per picker so a stale error from one sheet can't leak
  // into the other during a recovery bounce.
  const [rolePickerError, setRolePickerError] = useState<string | null>(null);
  const [arcPickerError, setArcPickerError] = useState<string | null>(null);
  const [pendingSubmission, setPendingSubmission] = useState<FailedSubmission | null>(null);
  // The Mood Ring arc the user picked, waiting to ride along with their next
  // contribution rather than being locked via the standalone endpoint (#545):
  // the arc is now locked server-side as a step of contributing, so a
  // space-scoped guest never touches the participation-gated arc route on their
  // first take. A ref (not state) so `submitOnlineEntries` reads the freshest
  // value without re-memoizing on every pick. Cleared once a submit consumes it.
  const pendingArcShapeRef = useRef<string | null>(null);
  const [proactivePromptedThreadId, setProactivePromptedThreadId] = useState<string | null>(null);
  // Count of offline-queue items that hit a persistent backend rejection
  // (e.g. template gate) on replay. Surfaced as a banner so the user can act.
  const [failedQueueCount, setFailedQueueCount] = useState(0);
  // Adaptive-prompt in-flight cue + "shaped by your story" affordance, driven by
  // the adaptive_prompts_ready socket event.
  const [promptsRefreshing, setPromptsRefreshing] = useState(false);
  const [promptsAdapted, setPromptsAdapted] = useState(false);

  const { showInstallPrompt, promptInstall, markContributed } = useInstallPrompt();

  const refreshThreadAndPrompts = useCallback(async (options: { surfaceErrors?: boolean } = {}) => {
    if (!threadId) return;

    // Await the thread first so we can branch on its template state. A
    // capture-first DRAFT (templateId == null) has no phases/prompts — the
    // backend throws on /prompts for it — so skip that request entirely and
    // let the render fall through to the draft "being set up" state.
    let loadedThread: Thread | null = null;
    try {
      loadedThread = await apiFetch<Thread>(`/threads/${threadId}`);
      setThread(loadedThread);
    } catch (err) {
      console.error('Failed to load thread', err);
      if (options.surfaceErrors) {
        setLoadError('Couldn\'t load this moment.');
      }
      return;
    }

    if (loadedThread.templateId == null) {
      // Draft (or otherwise template-less) thread: no prompts to fetch.
      setPrompts(null);
      return;
    }

    const phaseParam = new URLSearchParams(window.location.search).get('phase');
    const promptsUrl = phaseParam != null
      ? `/threads/${threadId}/prompts?phaseIndex=${phaseParam}`
      : `/threads/${threadId}/prompts`;
    try {
      const data = await apiFetch<PromptResponse>(promptsUrl);
      setPrompts(data);
    } catch (err) {
      console.warn('Failed to load prompts', err);
    }
  }, [threadId]);

  const loadThreadData = useCallback(async () => {
    if (!threadId) return;

    // Claim this load. preact-router keeps the page mounted across a threadId
    // change, so an in-flight brief request for the previous story can resolve
    // after the switch. Stamping the sequence here — before any await — means
    // the newest invocation always wins, whatever order the responses land in.
    const loadId = ++briefLoadRef.current;

    setLoading(true);
    setLoadError(null);

    const fetchContributions = apiFetchRaw<{ data: Contribution[]; cursor: string | null; hasMore: boolean }>(
      `/threads/${threadId}/contributions`,
    )
      .then(result => {
        setContributions(result.data ?? []);
        setNextCursor(result.cursor ?? null);
      })
      .catch(err => {
        console.error('Failed to load contributions', err);
      });

    await Promise.allSettled([
      refreshThreadAndPrompts({ surfaceErrors: true }),
      fetchContributions,
    ]);
    setLoading(false);

    // A newer load superseded this one while the awaits above were pending.
    // Bail rather than reopening the brief window for a story we left.
    if (briefLoadRef.current !== loadId) return;

    // Fetch the brief only. The read cursor is advanced by an explicit user
    // action (see acknowledgeBrief) — acknowledging here would clear the delta
    // for content nobody has looked at, which is the bug this replaces. The card
    // may still be below the fold, or the tab backgrounded, when this resolves.
    setBriefStatus('loading');
    guestApi.getStoryBrief(threadId)
      .then((brief) => {
        if (briefLoadRef.current !== loadId) return;
        setStoryBrief(brief);
        setBriefStatus('ready');
      })
      .catch(() => {
        if (briefLoadRef.current !== loadId) return;
        setStoryBrief(null);
        setBriefStatus('error');
      });
  }, [threadId, refreshThreadAndPrompts]);

  const {
    liveContributions,
    liveMessages,
    liveReaction,
    liveSessionId,
    presence,
    latestAuthor,
    clearLatestAuthor,
    sphereBalance,
    clearSphereBalance,
    adaptivePromptsReady,
    clearAdaptivePromptsReady,
    // The completion events. ThreadPage has consumed these all along, but a guest
    // who contributes and stays put — the common case, since /contribute is the
    // guest's whole world — never reaches ThreadPage, so the moment the thing they
    // fed actually finished was the one moment the product said nothing.
    phaseRecapReady,
    clearPhaseRecapReady,
    recapRenderComplete,
    clearRecapRenderComplete,
    recapRenderFailed,
    clearRecapRenderFailed,
    deliverableReady,
    clearDeliverableReady,
  } = useThreadEvents(threadId, {
    onTurnContextInvalidated: () => {
      refreshThreadAndPrompts().catch(err => {
        console.warn('Failed to refresh turn context', err);
      });
    },
  });

  // Only the mutable guards are refs: they must survive a re-render without
  // causing one, and a stale read is safe because both only ever move forward.
  // The brief itself is read from state — mirroring it into a ref lagged a
  // render tick, so a click landing in that window was silently dropped.
  const ackedTokenRef = useRef<string | null>(null);
  const ackInFlightRef = useRef(false);
  // Set when an acknowledgement is requested while the brief is still in flight.
  // The card's own actions cannot hit that window (they do not render until the
  // brief settles), but a submitted take can: the page-wide `loading` flag goes
  // false before the brief request even starts, so the composer is live while
  // briefStatus is still 'loading'. Without this the fast submitter's cursor
  // never advanced — the same "never acquires a cursor" failure this feature
  // exists to fix.
  const ackPendingRef = useRef(false);

  // Reset per-thread state when the page switches threads. preact-router keeps
  // this component mounted across a threadId change (the sibling reset at the
  // optimistic-index effect below assumes the same), so anything keyed to one
  // story has to be cleared by hand or it leaks into the next.
  //
  // hasContributed matters most: it only ever sets true, so carrying it over
  // would suppress onboarding on a genuinely new story.
  //
  // The brief is the other one that has to go, and for a sharper reason than
  // staleness: its `observedThrough` is a *cursor*. Left in place, the previous
  // story's card renders over the new one and its Got it / Jump in stamps that
  // cutoff onto THIS thread — burying everything the reader has not seen here,
  // which is exactly the loss the snapshot protocol exists to prevent. Back to
  // 'loading' rather than 'error' so onboarding does not flash in the gap.
  //
  // The three submission stashes are the same hazard aimed at a different
  // target — the story being written to, not the one being read:
  //  - pendingArcShapeRef would ride along with the next take and lock THIS
  //    thread's arc to a shape chosen for the previous one. Thread-wide and
  //    immutable once scored, so there is no undoing it.
  //  - pendingSubmission / failedSubmission are replayed by handleUnifiedSubmit,
  //    which always posts to the current threadId — so a take typed for the
  //    previous story would be published here. The arc picker's replay makes
  //    that reachable without the user touching a retry button.
  useEffect(() => {
    ackedTokenRef.current = null;
    ackInFlightRef.current = false;
    ackPendingRef.current = false;
    pendingArcShapeRef.current = null;
    setStoryBrief(null);
    setBriefStatus('loading');
    setCatchUpDismissed(false);
    setHasContributed(false);
    setContributedThisSession(false);
    setPendingSubmission(null);
    setFailedSubmission(null);
    // A sheet left open across the change belongs to the previous story but
    // writes to this one — a recovery-mode arc picker would apply its pick here.
    // The proactive effect below reopens it, in proactive mode, if this story
    // needs it.
    setRolePickerOpen(false);
    setArcPickerOpen(false);
  }, [threadId]);

  // Belt-and-braces over the reset effect and the load sequence: the brief is
  // only ever usable for the story it describes. A cross-thread brief is not a
  // cosmetic glitch — its cutoff would be written to the wrong cursor — so the
  // identity check lives here, once, where every consumer picks it up.
  const briefForThisThread = storyBrief && storyBrief.threadId === threadId ? storyBrief : null;

  /**
   * Advance the read cursor to the snapshot the reader was shown. Idempotent per
   * brief: repeated calls from Jump in / Got it / a submitted take collapse to
   * one request.
   */
  const acknowledgeBrief = useCallback(() => {
    if (!threadId) return;
    // Still fetching: a cutoff is imminent. Acknowledging now would send none
    // and let the server stamp `now`, burying content the reader never saw —
    // precisely what this protocol exists to prevent. The card's own actions
    // will acknowledge once the brief lands.
    if (briefStatus === 'loading') {
      ackPendingRef.current = true;
      return;
    }

    const token = briefForThisThread?.observedThrough;
    // No token means the fetch failed, or the backend predates the snapshot
    // protocol. Acknowledge unbounded: that matches the previous contract, and a
    // coarse cursor beats no cursor at all.
    const key = token ?? 'unbounded';
    if (ackInFlightRef.current || ackedTokenRef.current === key) return;

    ackInFlightRef.current = true;
    guestApi.markThreadRead(threadId, token)
      .then(() => { ackedTokenRef.current = key; })
      // Deliberately NOT latched on failure — the reader's next explicit action
      // retries. No timer, no backoff: the retry is their next gesture.
      .catch(() => {})
      .finally(() => { ackInFlightRef.current = false; });
  }, [threadId, briefStatus, briefForThisThread]);

  // Drain a deferred acknowledgement once the brief settles.
  useEffect(() => {
    if (briefStatus === 'loading' || !ackPendingRef.current) return;
    ackPendingRef.current = false;
    acknowledgeBrief();
  }, [briefStatus, acknowledgeBrief]);

  // One definition of "is there something to catch up on", shared with
  // CatchMeUpCard. Two copies would drift, and the drift shows up as both cards
  // rendering at once.
  //
  // Deliberately NOT folded together with `catchUpDismissed`: the two answer
  // different questions. Whether the CARD shows depends on dismissal; whether
  // this is a returning visitor at all does not. Gating onboarding on the
  // dismissable form would reveal a first-timer welcome the instant someone
  // dismissed their catch-up — which is the same double-card bug, delayed.
  const hasCatchUpDelta = hasCatchUp(briefForThisThread);
  const showCatchUp = hasCatchUpDelta && !catchUpDismissed;

  // Hydrate hasContributed from the server rather than leaving it session-local.
  //
  // ThreadWelcomeCard reads its `thread_welcome_seen:<id>` localStorage key
  // `&&`-gated on this flag, and it was only ever set by an in-session submit —
  // so on every fresh page load the guard short-circuited and onboarding
  // rendered expanded no matter how many times it had been dismissed. That is
  // why the double-card hit every returning visitor rather than only lurkers.
  //
  // myContributions is phase-scoped, so a contributor entering a NEW phase
  // still reads false here. That case is covered by the exclusivity gate above:
  // a new phase is itself a catch-up delta, so onboarding is suppressed anyway.
  useEffect(() => {
    const mine = prompts?.context?.phaseProgress?.myContributions ?? 0;
    if (mine > 0) setHasContributed(true);
  }, [prompts?.context?.phaseProgress?.myContributions]);

  useEffect(() => {
    loadThreadData();
    deleteExpiredDrafts().catch(() => {});
  }, [loadThreadData]);

  useEffect(() => {
    if (recapRenderComplete?.shareToken) {
      setRenderedHighlightToken(recapRenderComplete.shareToken);
    }
  }, [recapRenderComplete]);

  // Adaptive (story-tailored) prompts finished generating — refetch so the
  // contributor sees them without a manual reload. `isAdapted=false` means no
  // tailoring is coming, so we simply stop listening (no refetch, no cue).
  useEffect(() => {
    if (!adaptivePromptsReady) return;
    const currentPhaseIndex = prompts?.phaseIndex;
    // Ignore events for a phase other than the one being viewed.
    if (currentPhaseIndex != null && adaptivePromptsReady.phaseIndex !== currentPhaseIndex) {
      clearAdaptivePromptsReady();
      return;
    }
    const adapted = adaptivePromptsReady.isAdapted;
    clearAdaptivePromptsReady();
    if (!adapted) return;
    setPromptsRefreshing(true);
    refreshThreadAndPrompts()
      .then(() => setPromptsAdapted(true))
      .catch(err => console.warn('Failed to refresh adaptive prompts', err))
      .finally(() => setPromptsRefreshing(false));
  }, [adaptivePromptsReady, prompts?.phaseIndex, refreshThreadAndPrompts, clearAdaptivePromptsReady]);

  // Reset the live-event "adapted" flag whenever the served phase changes, so the
  // chip can't carry over from an adapted phase onto a non-adapted one. The
  // loaded-state path (prompts.promptAdaptation) still drives the chip per-phase.
  useEffect(() => {
    setPromptsAdapted(false);
  }, [prompts?.phaseIndex]);

  useEffect(() => {
    setOptimisticCreatedIndices(new Set());
    setQueuedPromptIndices(new Set());
    setOptimisticMyContributionCount(null);
    setPromptLifecycleLocked(false);
  }, [threadId, prompts?.phaseIndex]);

  useEffect(() => {
    const confirmedCount = prompts?.context.phaseProgress.myContributions;
    if (confirmedCount == null) return;
    setOptimisticMyContributionCount((current) => current != null && current > confirmedCount ? current : null);
  }, [prompts?.context.phaseProgress.myContributions]);

  useEffect(() => {
    const promptCount = prompts?.prompts.length ?? 0;
    const confirmed = new Set(
      (prompts?.promptCompletion ?? [])
        .filter((item) => item.answered)
        .map((item) => item.promptIndex),
    );
    setOptimisticCreatedIndices((current) => new Set(
      [...current].filter((index) => index < promptCount && !confirmed.has(index)),
    ));
    setQueuedPromptIndices((current) => new Set(
      [...current].filter((index) => index < promptCount && !confirmed.has(index)),
    ));
  }, [prompts?.phaseIndex, prompts?.prompts.length, prompts?.promptCompletion]);

  const promptFlowStates = useMemo(() => {
    const states = new Map<number, PromptFlowState>();
    prompts?.prompts.forEach((_, index) => states.set(index, 'unanswered'));
    for (const item of prompts?.promptCompletion ?? []) {
      if (item.answered) states.set(item.promptIndex, 'answered');
    }
    for (const index of optimisticCreatedIndices) states.set(index, 'answered');
    for (const index of queuedPromptIndices) {
      if (states.get(index) !== 'answered') states.set(index, 'queued');
    }
    return states;
  }, [prompts?.prompts, prompts?.promptCompletion, optimisticCreatedIndices, queuedPromptIndices]);

  // Subscribe to offline-queue persistent failures so we can surface a banner.
  useEffect(() => {
    getFailedQueueCount().then(setFailedQueueCount).catch(() => {});
    const onFail = (event: Event) => {
      const ce = event as CustomEvent<{ count: number }>;
      if (ce.detail?.count != null) setFailedQueueCount(ce.detail.count);
      void getFailed().then((items) => {
        const failed = new Set(items
          .filter((item) => item.threadId === threadId)
          .flatMap((item) => typeof item.metadata?.promptIndex === 'number'
            ? [item.metadata.promptIndex]
            : []));
        if (failed.size > 0) {
          trackCaptureEvent('prompt_sync_failed', {
            surface: 'prompt-flow',
            threadId,
            count: failed.size,
          });
        }
        setQueuedPromptIndices((current) => new Set([...current].filter((index) => !failed.has(index))));
      }).catch(() => {});
    };
    const onOnline = () => {
      const pendingPromptCount = queuedPromptIndices.size;
      void syncPendingContributions()
        .then(() => refreshThreadAndPrompts())
        .then(() => {
          if (pendingPromptCount > 0) {
            trackCaptureEvent('prompt_sync_confirmed', {
              surface: 'prompt-flow',
              threadId,
              count: pendingPromptCount,
            });
          }
        })
        .catch(() => {});
    };
    window.addEventListener(QUEUE_FAILURE_EVENT, onFail);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener(QUEUE_FAILURE_EVENT, onFail);
      window.removeEventListener('online', onOnline);
    };
  }, [threadId, refreshThreadAndPrompts, queuedPromptIndices]);

  const loadMore = async () => {
    if (!nextCursor || !threadId) return;
    try {
      const result = await apiFetchRaw<{ data: Contribution[]; cursor: string | null; hasMore: boolean }>(
        `/threads/${threadId}/contributions?cursor=${nextCursor}`,
      );
      setContributions(prev => [...prev, ...(result.data ?? [])]);
      setNextCursor(result.cursor ?? null);
    } catch {
      // silently fail
    }
  };

  // Merge REST contributions with live WebSocket contributions (live first, deduped)
  const allContributions = useMemo(() => {
    const existingIds = new Set(contributions.map(c => c.id));
    const newLive = liveContributions.filter(c => !existingIds.has(c.id));
    return [...newLive, ...contributions];
  }, [contributions, liveContributions]);

  const handleToastDismiss = useCallback(() => clearLatestAuthor(), [clearLatestAuthor]);

  const handleBuildOn = useCallback((contribution: Contribution) => {
    setBuildOnTarget(contribution);
    setSheetOpen(true);
  }, []);

  const handleSheetClose = useCallback(() => {
    setSheetOpen(false);
    setBuildOnTarget(null);
  }, []);

  // Template-gate context derived from the prompts response (PromptEngineService
  // surfaces these per-user). Used both for proactive prompting and for deciding
  // when to disable the composer (Hot Seat / Campfire Circle).
  // Capture-first DRAFT predicate — mirrors the backend exactly (templateId
  // falsy AND captureFirst.status in {capturing, proposed}). A formalized draft
  // already has a templateId, so it flows through the normal prompt UI.
  const captureFirstStatus = thread?.settings?.captureFirst?.status ?? null;
  const isCaptureDraft =
    thread != null &&
    thread.templateId == null &&
    (captureFirstStatus === 'capturing' || captureFirstStatus === 'proposed');

  const templateSlug = prompts?.context?.templateSlug ?? null;
  const myRole = prompts?.context?.myRole ?? null;
  const arcTargetShape = prompts?.context?.emotionalArc?.targetShape ?? null;
  const isMyTurn = prompts?.context?.isMyTurn;
  const isPlotArmor = templateSlug === 'prophecy_fulfillment';
  const isMoodRing = templateSlug === 'emotional_spiral';
  const isHotSeat = templateSlug === 'campfire_circle';
  const hotSeatBlocked = isHotSeat && isMyTurn === false;

  // Occupancy per Proppian sphere (role name → how many contributors hold it).
  // Spheres are shareable now — this drives the gentle "who's here" hint and the
  // empty-sphere invitation, never a gate. Derived from templateState.roles,
  // which maps userId → role label.
  const roleOccupancy = useMemo(() => {
    const roles = thread?.templateState?.roles;
    const counts: Record<string, number> = {};
    if (roles) {
      for (const roleName of Object.values(roles)) {
        counts[roleName] = (counts[roleName] ?? 0) + 1;
      }
    }
    return counts;
  }, [thread?.templateState?.roles]);

  // Open the proactive picker once per thread mount when the template requires
  // a selection the user hasn't made yet. Reactive recovery is handled inside
  // submitOnlineEntries' catch block.
  useEffect(() => {
    if (!thread || !prompts || !threadId) return;
    if (proactivePromptedThreadId === threadId) return;

    // Treat both null and the backend's empty-string default as "not yet selected".
    const arcUnset = arcTargetShape == null || arcTargetShape === '';
    if (isPlotArmor && !myRole) {
      setRolePickerMode('proactive');
      setRolePickerError(null);
      setRolePickerOpen(true);
      setProactivePromptedThreadId(threadId);
    } else if (isMoodRing && arcUnset) {
      setArcPickerMode('proactive');
      setArcPickerError(null);
      setArcPickerOpen(true);
      setProactivePromptedThreadId(threadId);
    }
  }, [thread, prompts, threadId, proactivePromptedThreadId, isPlotArmor, isMoodRing, myRole, arcTargetShape]);

  const submitOnlineEntries = useCallback(async (
    entries: UnifiedSubmitEntry[],
    buildsOnContributionId?: string,
  ): Promise<SubmitResult> => {
    const failedResult = (delivery: 'failed' | 'deferred' = 'failed'): SubmitResult => ({
      delivery,
      createdIndices: [],
      queuedIndices: [],
      failedIndices: delivery === 'failed'
        ? entries.flatMap((entry) => entry.promptMeta ? [entry.promptMeta.promptIndex] : [])
        : [],
      createdCount: 0,
      next: null,
    });
    if (!threadId) return failedResult();

    setSubmittingGlobal(true);
    setUploadError(null);
    setUploadProgress(null);

    try {
      const contributionsLocal: Array<Record<string, unknown>> = [];
      let droppedEntry = false;

      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const trimmedContent = entry.content.trim();
        let mediaUrl: string | undefined;
        let mediaThumbnailUrl: string | undefined;
        let cType: 'text' | 'photo' | 'audio' | 'video' = 'text';

        if (entry.mediaFile && entry.mediaType) {
          if (entry.mediaFile.size === 0) {
            console.warn(`[ContributePage] Skipping entry ${i}: zero-size media file`);
            droppedEntry = true;
            continue;
          }
          cType = entry.mediaType;
          const mediaSignal = AbortSignal.timeout(120_000);
          const thumbSignal = AbortSignal.timeout(30_000);
          if (entry.mediaType === 'photo') {
            mediaUrl = await uploadPhoto(
              threadId,
              entry.mediaFile,
              entry.mediaFilename ?? `photo-${Date.now()}.jpg`,
              resolveUploadContentType(entry.mediaContentType, entry.mediaFile, 'image/jpeg'),
              thumbSignal
            );
          } else {
            const fallback = entry.mediaType === 'audio' ? 'audio/webm' : 'video/webm';
            trackCaptureEvent('upload_started', { mediaType: entry.mediaType, threadId, spaceId: thread?.spaceId });

            if (entry.mediaType === 'video') {
              setUploadProgress(0);
              mediaUrl = await uploadMediaWithProgress(
                threadId,
                entry.mediaFile,
                entry.mediaFilename ?? `video-${Date.now()}.webm`,
                resolveUploadContentType(entry.mediaContentType, entry.mediaFile, fallback),
                (progress) => setUploadProgress(progress),
              );
              setUploadProgress(null);
            } else {
              mediaUrl = await uploadMedia(
                threadId,
                entry.mediaFile,
                entry.mediaFilename ?? `${entry.mediaType}-${Date.now()}.webm`,
                resolveUploadContentType(entry.mediaContentType, entry.mediaFile, fallback),
                mediaSignal,
              );
            }

            if (entry.mediaType === 'video' && entry.mediaThumbnailFile) {
              try {
                mediaThumbnailUrl = await uploadPhoto(
                  threadId,
                  entry.mediaThumbnailFile,
                  entry.mediaThumbnailFilename ?? `video-thumb-${Date.now()}.jpg`,
                  entry.mediaThumbnailFile.type || 'image/jpeg',
                  thumbSignal
                );
              } catch (thumbnailError) {
                console.warn('[ContributePage] Video thumbnail upload failed, continuing without thumbnail', thumbnailError);
                mediaThumbnailUrl = undefined;
              }
            }
            trackCaptureEvent('upload_completed', { mediaType: entry.mediaType, threadId, spaceId: thread?.spaceId });
          }
        } else if (trimmedContent) {
          cType = 'text';
        } else {
          droppedEntry = true;
          continue;
        }

        const body: Record<string, unknown> = {
          contributionType: cType,
          content: trimmedContent || undefined,
          visibility: entry.visibility,
        };

        if (mediaUrl && entry.mediaFile) {
          body.mediaUrl = mediaUrl;
          body.mediaType = resolveUploadContentType(
            entry.mediaContentType,
            entry.mediaFile,
            entry.mediaType === 'photo' ? 'image/jpeg' : entry.mediaType === 'audio' ? 'audio/webm' : 'video/webm',
          );
        }
        if (entry.mediaDuration != null) {
          body.mediaDuration = entry.mediaDuration;
        }
        if (mediaThumbnailUrl) {
          body.mediaThumbnailUrl = mediaThumbnailUrl;
        }
        if (entry.mediaWidth != null) {
          body.mediaWidth = entry.mediaWidth;
        }
        if (entry.mediaHeight != null) {
          body.mediaHeight = entry.mediaHeight;
        }
        if (buildsOnContributionId && i === 0) {
          body.buildsOnContributionId = buildsOnContributionId;
        }
        const metadata = buildContributionMetadata(entry);
        if (metadata) {
          body.metadata = metadata;
        }
        contributionsLocal.push(body);
      }

      // Ride the pending Mood Ring arc selection along with the first take so the
      // server locks it as a step of contributing (#545). Thread-level, so it
      // only needs to land on the first contribution; the server ignores it once
      // the arc is set. Not cleared here — cleared only after the POST resolves,
      // so a failed submit keeps the selection for the retry.
      const arcShapeToSend = pendingArcShapeRef.current;
      if (arcShapeToSend && contributionsLocal.length > 0) {
        contributionsLocal[0].arcShape = arcShapeToSend;
      }

      let created: Contribution[] = [];
      let failedIndices: number[] = [];
      if (contributionsLocal.length === 1) {
        const contribution = await apiFetch<Contribution>(`/threads/${threadId}/contributions`, {
          method: 'POST',
          body: JSON.stringify(contributionsLocal[0]),
        });
        created = [contribution];
        setContributions(prev => [contribution, ...prev]);
      } else if (contributionsLocal.length > 1) {
        const result = await apiFetchRaw<{ data: Contribution[]; error?: { failedIndex: number; message: string; code?: string } }>(
          `/threads/${threadId}/contributions/batch`,
          { method: 'POST', body: JSON.stringify({ contributions: contributionsLocal }) },
        );
        created = result.data ?? [];
        if (created.length > 0) {
          setContributions(prev => [...created, ...prev]);
        }
        if (result.error) {
          failedIndices = contributionsLocal
            .slice(result.error.failedIndex)
            .flatMap((body) => {
              const metadata = body.metadata as Record<string, unknown> | undefined;
              return typeof metadata?.promptIndex === 'number' ? [metadata.promptIndex] : [];
            });
          setUploadError(result.error.message);
        }
      } else {
        setUploadError('Nothing could be shared. Check the attachment and try again.');
        return failedResult();
      }

      setFailedSubmission(null);
      // The take posted, so the arc it carried is now locked server-side; drop
      // the pending selection so a later unrelated submit doesn't re-send it.
      // Not an invariant — a pick that only reached the offline queue via
      // `setArcShapeForThread` leaves the ref set until some submit consumes it.
      // Re-sending is harmless: the server ignores `arcShape` once the arc is
      // set, so this is tidiness, not correctness.
      pendingArcShapeRef.current = null;
      if (failedIndices.length === 0 && !droppedEntry) setUploadError(null);
      const createdIndices = created.flatMap((contribution) =>
        typeof contribution.metadata?.promptIndex === 'number'
          ? [contribution.metadata.promptIndex]
          : [],
      );
      return {
        delivery: failedIndices.length > 0 || droppedEntry ? 'partial' : 'posted',
        createdIndices,
        queuedIndices: [],
        failedIndices,
        createdCount: created.length,
        next: null,
      };
    } catch (err) {
      trackCaptureEvent('upload_failed', { reason: err instanceof Error ? err.message : 'unknown_error', threadId, spaceId: thread?.spaceId });
      setUploadProgress(null);
      if (err instanceof ApiError && err.code === 'QUOTA_EXCEEDED') {
        setFailedSubmission(null);
        navigate('/subscription');
        throw new Error('Free tier limit reached. Upgrade to continue contributing.');
      }
      // Recoverable template gates: open a picker, save the in-flight submission
      // so the user can keep their typed take after assigning role / arc.
      if (isRoleSelectionRequired(err)) {
        setPendingSubmission({ entries, buildsOnContributionId });
        setRolePickerMode('recovery');
        setRolePickerError(null);
        setRolePickerOpen(true);
        return failedResult('deferred');
      }
      if (isArcSelectionRequired(err)) {
        // Picking an arc below repairs `templateState.arcState.targetShape` as a
        // side effect (POST .../arc runs syncTemplateArcState), so a creation-time
        // seeding regression looks like a normal picker step instead of a bug.
        // `hasArcRow` is the tell: the thread already has an arc on
        // `thread_emotional_arcs`, yet the gate still fired — that combination
        // means the two sources of truth diverged at creation.
        const createdAtMs = thread?.createdAt ? Date.parse(thread.createdAt) : NaN;
        trackCaptureEvent('arc_selection_recovered', {
          surface: 'contribute',
          threadId,
          spaceId: thread?.spaceId,
          hasArcRow: thread?.emotionalArcShape != null,
          threadAgeSeconds: Number.isNaN(createdAtMs)
            ? undefined
            : Math.round((Date.now() - createdAtMs) / 1000),
        });
        setPendingSubmission({ entries, buildsOnContributionId });
        setArcPickerMode('recovery');
        setArcPickerError(null);
        setArcPickerOpen(true);
        return failedResult('deferred');
      }
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setFailedSubmission({ entries, buildsOnContributionId });
      return failedResult();
    } finally {
      setSubmittingGlobal(false);
    }
  }, [threadId]);

  const handleUnifiedSubmit = async (
    entries: UnifiedSubmitEntry[],
    buildsOnContributionId = buildOnTarget?.id,
  ): Promise<SubmitResult> => {
    const failed = (): SubmitResult => ({
      delivery: 'failed',
      createdIndices: [],
      queuedIndices: [],
      failedIndices: entries.flatMap((entry) => entry.promptMeta ? [entry.promptMeta.promptIndex] : []),
      createdCount: 0,
      next: null,
    });
    if (!threadId) return failed();
    let result: SubmitResult;

    if (navigator.onLine) {
      result = await submitOnlineEntries(entries, buildsOnContributionId);
    } else {
      // Offline flow
      const batchTime = Date.now();
      const queuedIndices: number[] = [];
      let queuedCount = 0;
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const trimmedContent = entry.content.trim();
        let cType: 'text'|'photo'|'audio'|'video' = 'text';
        let mediaBlob: Blob | undefined;
        let mediaFilename: string | undefined;
        let mediaContentType: string | undefined;
        let mediaThumbnailBlob: Blob | undefined;
        let mediaThumbnailFilename: string | undefined;
        let mediaThumbnailContentType: string | undefined;

        if (entry.mediaFile && entry.mediaType) {
          cType = entry.mediaType;
          mediaBlob = entry.mediaFile;
          mediaFilename = entry.mediaFilename
            ?? (entry.mediaType === 'photo' ? `photo-${Date.now()}.jpg` : `${entry.mediaType}-${Date.now()}.webm`);
          mediaContentType = resolveUploadContentType(
            entry.mediaContentType,
            entry.mediaFile,
            entry.mediaType === 'photo' ? 'image/jpeg' : entry.mediaType === 'audio' ? 'audio/webm' : 'video/webm',
          );
          if (entry.mediaType === 'video' && entry.mediaThumbnailFile) {
            mediaThumbnailBlob = entry.mediaThumbnailFile;
            mediaThumbnailFilename = entry.mediaThumbnailFilename ?? `video-thumb-${Date.now()}.jpg`;
            mediaThumbnailContentType = entry.mediaThumbnailFile.type || 'image/jpeg';
          }
        } else if (!trimmedContent) {
          continue;
        }

        await addToQueue({
          id: crypto.randomUUID?.() ?? `${batchTime}-${i}`,
          threadId,
          contributionType: cType,
          content: trimmedContent || undefined,
          mediaBlob,
          mediaFilename,
          mediaContentType,
          mediaDuration: entry.mediaDuration,
          mediaThumbnailBlob,
          mediaThumbnailFilename,
          mediaThumbnailContentType,
          mediaWidth: entry.mediaWidth,
          mediaHeight: entry.mediaHeight,
          visibility: entry.visibility,
          buildsOnContributionId: buildOnTarget && i === 0 ? buildOnTarget.id : undefined,
          metadata: buildContributionMetadata(entry),
          // Carry the pending Mood Ring arc on the first queued take (thread-level,
          // server locks it best-effort on sync) so a pick-then-offline first take
          // still lands its arc (#545). `queuedCount === 0` is the first item
          // actually queued, tolerating leading entries skipped for empty content.
          arcShape: queuedCount === 0 ? (pendingArcShapeRef.current ?? undefined) : undefined,
          createdAt: new Date(batchTime + i).toISOString(),
          synced: false,
        });
        queuedCount += 1;
        if (entry.promptMeta) queuedIndices.push(entry.promptMeta.promptIndex);
      }
      // The selection is now in the queue; drop it so a later online submit
      // doesn't re-send a shape the sync will already have locked.
      if (queuedCount > 0) pendingArcShapeRef.current = null;
      result = queuedCount === 0
        ? failed()
        : {
            delivery: 'queued',
            createdIndices: [],
            queuedIndices,
            failedIndices: [],
            createdCount: 0,
            next: null,
          };
    }

    if (result.delivery === 'failed' || result.delivery === 'deferred') {
      return result;
    }

    if (result.createdIndices.length > 0) {
      setOptimisticCreatedIndices((current) => new Set([...current, ...result.createdIndices]));
    }
    if (result.createdCount > 0) {
      const confirmedCount = prompts?.context.phaseProgress.myContributions ?? 0;
      setOptimisticMyContributionCount((current) => Math.max(current ?? confirmedCount, confirmedCount) + result.createdCount);
    }
    if (result.queuedIndices.length > 0) {
      setQueuedPromptIndices((current) => new Set([...current, ...result.queuedIndices]));
    }

    if (result.delivery === 'partial') {
      markContributed();
      setHasContributed(true);
      setContributedThisSession(true);
      // A submitted take is unambiguous engagement with the current content, and
      // it is the baseline for a first visit that never had a cursor. Idempotent
      // via the token guard, so this is a no-op if the card already acked.
      acknowledgeBrief();
      return result;
    }

    const submittedHasPrompt = entries.some((entry) => entry.promptMeta != null);
    const answeredForNavigation = new Set<number>();
    for (const item of prompts?.promptCompletion ?? []) {
      if (item.answered) answeredForNavigation.add(item.promptIndex);
    }
    for (const index of optimisticCreatedIndices) answeredForNavigation.add(index);
    for (const index of queuedPromptIndices) answeredForNavigation.add(index);
    for (const index of result.createdIndices) answeredForNavigation.add(index);
    for (const index of result.queuedIndices) answeredForNavigation.add(index);

    const maxAllowed = prompts?.context.phaseProgress.maxAllowed ?? null;
    const myContributions = prompts?.context.phaseProgress.myContributions ?? 0;
    // A queued response still consumes phase quota once it syncs, so it has to count here or
    // an offline user gets advanced into work the per-author cap will reject. Server-confirmed
    // indices are dropped from queuedPromptIndices as they land, so this never double-counts
    // what myContributions already includes.
    const pendingQueuedCount = new Set([...queuedPromptIndices, ...result.queuedIndices]).size;
    // maxAllowed is per requester and per phase; see
    // storythreads-api/src/services/templateStrategies/DefaultTemplateStrategy.ts.
    const confirmedQuotaReached = maxAllowed != null
      && myContributions + result.createdCount >= maxAllowed;
    if (prompts?.completed) {
      result.next = { kind: 'storyCompleted' };
    } else if (maxAllowed != null && myContributions + result.createdCount + pendingQueuedCount >= maxAllowed) {
      result.next = { kind: 'phaseQuotaReached' };
    } else if (!prompts || !submittedHasPrompt || prompts.prompts.length === 1 || buildOnTarget) {
      result.next = { kind: 'ordinary' };
    } else {
      const nextIndex = prompts.prompts.findIndex((_, index) => !answeredForNavigation.has(index));
      result.next = nextIndex >= 0
        ? { kind: 'advance', toIndex: nextIndex }
        : { kind: 'coverageComplete' };
    }

    // Latch only on quota the server has confirmed. A queued response can still be revoked
    // (the sync-failure path drops its index from queuedPromptIndices), and this lock has no
    // unlock short of a phase change — latching on it would strand the user outside a phase
    // whose quota came back. draftsPostable tracks the queued count live and self-heals.
    if ((result.next.kind === 'phaseQuotaReached' && confirmedQuotaReached)
      || result.next.kind === 'storyCompleted') {
      setPromptLifecycleLocked(true);
    }

    markContributed();
    setHasContributed(true);
    setContributedThisSession(true);
    // Same baseline as the partial-delivery branch above. Also correct for a
    // queued (offline) take: the cursor records what the reader SAW, not what
    // the server has stored.
    acknowledgeBrief();
    setBuildOnTarget(null);
    // Every terminal branch now holds the sheet open for the success screen —
    // `ordinary` included, which used to close it here and send the guest away
    // with no acknowledgment that their take had landed at all.
    if (result.delivery === 'posted') {
      void refreshThreadAndPrompts();
    }

    // Clean up any video draft for this thread after successful submission
    if (threadId) {
      getDraft(threadId).then(draft => {
        if (draft) deleteDraft(draft.id);
      }).catch(() => {});
    }

    return result;
  };

  // Refetch thread + prompts so templateState.roles / prompts.context.myRole
  // reflect any updates made by other tabs (or our own optimistic actions).
  const refetchTemplateContext = useCallback(async () => {
    if (!threadId) return;
    try {
      const nextThread = await apiFetch<Thread>(`/threads/${threadId}`);
      setThread(nextThread);
      // Capture-first draft: no template yet → /prompts would 4xx
      // (PromptEngineService throws on a null-template thread). Skip the fetch
      // and clear any stale prompts, mirroring refreshThreadAndPrompts.
      if (nextThread.templateId == null) {
        setPrompts(null);
        return;
      }
      const nextPrompts = await apiFetch<PromptResponse>(`/threads/${threadId}/prompts`);
      setPrompts(nextPrompts);
    } catch (err) {
      console.warn('[ContributePage] refetchTemplateContext failed', err);
    }
  }, [threadId]);

  const replayPendingSubmission = useCallback(async () => {
    if (!pendingSubmission) return;
    const result = await handleUnifiedSubmit(
      pendingSubmission.entries,
      pendingSubmission.buildsOnContributionId,
    );
    if (result.delivery === 'posted' || result.delivery === 'queued' || result.delivery === 'partial') {
      setPendingSubmission(null);
    }
  }, [pendingSubmission, handleUnifiedSubmit]);

  // After a successful template-gate recovery (role assigned / arc locked),
  // unblock any queued contributions for this thread that were previously
  // parked with a persistent `lastError`. Without this, items captured offline
  // before the gate was cleared would remain permanently stuck.
  const unblockFailedQueueItems = useCallback(async () => {
    if (!threadId) return;
    try {
      const cleared = await clearFailedForThread(threadId);
      if (cleared > 0) {
        const remaining = await getFailedQueueCount().catch(() => 0);
        setFailedQueueCount(remaining);
        if (navigator.onLine) {
          // Fire-and-forget: any further failures will re-mark via markFailed.
          void syncPendingContributions().catch(() => {});
        }
      }
    } catch (err) {
      console.warn('[ContributePage] unblockFailedQueueItems failed', err);
    }
  }, [threadId]);

  const handleRoleSelected = useCallback(async (role: string) => {
    if (!threadId) return;
    setRolePickerError(null);
    try {
      await templateApi.assignRole(threadId, role);
      setRolePickerOpen(false);
      await refetchTemplateContext();
      await unblockFailedQueueItems();
      await replayPendingSubmission();
    } catch (err) {
      if (isRoleAlreadyTaken(err) || isStaleTemplateState(err)) {
        // Someone else (or another tab) just took it. Refresh and re-prompt.
        await refetchTemplateContext();
        setRolePickerError('That role was just taken. Pick another one.');
      } else if (err instanceof ApiError) {
        setRolePickerError(err.message);
      } else {
        setRolePickerError('Could not assign the role. Try again.');
      }
    }
  }, [threadId, refetchTemplateContext, replayPendingSubmission, unblockFailedQueueItems]);

  const handleArcSelected = useCallback(async (arcKey: string) => {
    if (!threadId) return;
    setArcPickerError(null);
    // Stash the pick instead of locking it via the standalone, participation-
    // gated arc endpoint (#545). The contribution carries `arcShape` and the
    // server locks it once that take has persisted, so a guest never hits that
    // gate on their first take, and a pick the user abandons — or one whose take
    // is rejected — never orphans a thread-wide arc lock. Proactive: the next
    // manual submit reads the ref. Recovery: the replayed pending submission
    // carries it. If the arc is already locked (someone else got there first),
    // the server keeps the existing shape and the take still posts — no error to
    // surface here.
    pendingArcShapeRef.current = arcKey;
    setArcPickerOpen(false);
    // Stamp the pick onto anything already queued for this thread. A take
    // captured offline *before* the pick carries no `arcShape`, and unblocking
    // only clears its error marker — without this it would fail the arc gate on
    // every replay forever, because nothing else ever locks the arc for it.
    try {
      await setArcShapeForThread(threadId, arcKey);
    } catch (err) {
      console.warn('[ContributePage] setArcShapeForThread failed', err);
    }
    // Replay before unblocking. Nothing has locked the arc server-side yet — the
    // replayed take is what locks it — so unblocking first would fire a sync
    // against a still-unset arc, fail every queued item on the gate and
    // immediately re-mark them (a visible 1 → 0 → 1 flicker on the stuck-items
    // banner). Caught, not just `finally`d: this runs straight off the picker's
    // onSelect, and the sheet is already closed, so a rejection here has nowhere
    // to render — it would surface as an unhandled rejection instead.
    try {
      await replayPendingSubmission();
    } catch (err) {
      console.warn('[ContributePage] replayPendingSubmission failed', err);
    } finally {
      await unblockFailedQueueItems();
    }
  }, [threadId, replayPendingSubmission, unblockFailedQueueItems]);

  const handleRolePickerDismiss = useCallback(() => {
    setRolePickerOpen(false);
    setRolePickerError(null);
    // Proactive dismiss must drop any stale pendingSubmission so a later
    // proactive re-fire can't silently replay an old failed submission.
    // Recovery dismiss keeps pendingSubmission so the retry banner can still replay.
    if (rolePickerMode === 'proactive') {
      setPendingSubmission(null);
    }
  }, [rolePickerMode]);

  const handleArcPickerDismiss = useCallback(() => {
    setArcPickerOpen(false);
    setArcPickerError(null);
    if (arcPickerMode === 'proactive') {
      setPendingSubmission(null);
    }
  }, [arcPickerMode]);

  const handleRetryUpload = async () => {
    if (!failedSubmission || !navigator.onLine) return;

    await handleUnifiedSubmit(failedSubmission.entries, failedSubmission.buildsOnContributionId);
  };

  if (!threadId) {
    return <div class={styles.notFound}>Moment not found</div>;
  }

  if (loading) {
    return (
      <div class={styles.loadingContainer}>
        <Spinner size={28} />
        <p class={styles.loadingText}>Loading moment...</p>
      </div>
    );
  }

  if (loadError && !thread) {
    return (
      <div class={styles.errorContainer}>
        <h3>Couldn't load this moment</h3>
        <p>{loadError}</p>
        <button class={styles.retryButton} onClick={loadThreadData}>Try Again</button>
      </div>
    );
  }

  // Capture-first DRAFT: the thread has no template yet, so there are no phases,
  // prompts, roles, or arc to scaffold. Render a warm "being set up" state.
  // While `capturing`, the crew is still adding takes async, so we keep the
  // basic compose affordances (feed + FAB + sheet) live. While `proposed`, the
  // story is being shaped — show a read-only feed and ask people to check back.
  if (isCaptureDraft) {
    const isCapturing = captureFirstStatus === 'capturing';
    const draftHeading = isCapturing
      ? 'This moment is still being captured'
      : 'We’re shaping this into a story';
    const draftBody = isCapturing
      ? 'Your crew is capturing this live. Add your take — photos, video, a voice note, or a few words — and it’ll all become part of the story.'
      : 'Hang tight — we’re turning everyone’s takes into a story. Check back in a moment.';

    return (
      <div class={styles.pageContainer}>
        <StaleCacheBanner />
        <ChorusHeader back title={thread?.title ?? 'Moment'} onBack={() => navigate('/')} />

        <div class={styles.emptyFeed}>
          <div class={styles.emptyIcon}>
            <ChorusEmpty size={140} variant="aligned" />
          </div>
          <h3 class={styles.emptyHeading}>{draftHeading}</h3>
          <p class={styles.emptyDescription}>{draftBody}</p>
        </div>

        {/* Read-only feed of what's been captured so far (both states). */}
        {allContributions.length > 0 && (
          <div class={styles.feed}>
            <ThreadedContributionList
              contributions={allContributions}
              onBuildOn={handleBuildOn}
            />
            {nextCursor && (
              <button onClick={loadMore} class={styles.loadMoreButton}>
                Load More
              </button>
            )}
          </div>
        )}

        {/* CAPTURE_FIRST_STORY_FLOW — live banter + reactions while a session is
            active. Guests join the chat (text/emoji); live voice stays iOS crew.
            The session id is learned from the live:* events the guest receives. */}
        {isCapturing && liveSessionId && (
          <div class={styles.feed} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--st-space-3)' }}>
            <LiveChatPanel sessionId={liveSessionId} messages={liveMessages} />
            <LiveReactionLayer sessionId={liveSessionId} ping={liveReaction} />
          </div>
        )}

        {/* Upload progress / error surface for in-flight capturing submissions. */}
        {uploadProgress !== null && (
          <div class={styles.uploadProgress}>
            <div class={styles.uploadProgressBar} style={{ width: `${uploadProgress * 100}%` }} />
            <span class={styles.uploadProgressText}>Uploading... {Math.round(uploadProgress * 100)}%</span>
          </div>
        )}

        {/* Compose is available only while capturing (no phase/prompt scaffolding,
            no template gates — guests/members just add takes). */}
        {isCapturing && (
          <>
            <button class={styles.fab} onClick={() => setSheetOpen(true)}>
              <IconPlus size={16} />
              {allContributions.length === 0 ? 'Add the First Take' : 'Add Your Take'}
            </button>
            <ContributeSheet
              open={sheetOpen}
              onClose={handleSheetClose}
              onUnifiedSubmit={handleUnifiedSubmit}
              submitting={submittingGlobal}
              threadId={threadId}
              buildOnTarget={buildOnTarget}
              onClearBuildOn={() => setBuildOnTarget(null)}
              threadType={thread?.threadType ?? undefined}
              storyContext={thread?.storyContext}
              momentTitle={thread?.title}
              onViewChorus={() => navigate(`/thread/${threadId}`)}
            />
          </>
        )}
      </div>
    );
  }

  /**
   * Routes to the finished artifact. The phase-recap page is guest-permitted
   * (`/v2/spaces/:spaceId/threads/:threadId/phase-recaps/:phaseIndex`) but needs a
   * spaceId, which only exists once the thread snapshot has loaded — so this falls
   * back to the thread itself rather than leaving the guest holding a toast with a
   * button that does nothing.
   */
  const goToPayoff = (phase: number | 'cumulative') => {
    navigate(
      thread?.spaceId
        ? `/spaces/${thread.spaceId}/threads/${threadId}/phase-recap/${phase}`
        : `/thread/${threadId}`,
    );
  };

  /**
   * Hands each visible toast its slot in the stack. Several of these can be true
   * at once — another take landing while a render finishes is ordinary — and the
   * actionable ones no longer expire on their own, so without this the second one
   * covers the first indefinitely. Order is announcement order; the counter only
   * advances for toasts that actually render.
   */
  let toastSlot = 0;
  const nextToastSlot = () => toastSlot++;

  /**
   * The reel, if we've been handed the token that reaches it. The success
   * panel's "Watch the Highlight" makes the same promise the toast does, so it
   * resolves the same way — the player when we can, the written recap when we
   * can't. `renderedHighlightToken` latches the token rather than reading the
   * live event, which is cleared the moment its toast is dismissed.
   */
  const goToHighlight = () => {
    if (renderedHighlightToken) {
      navigate(`/r/${encodeURIComponent(renderedHighlightToken)}`);
      return;
    }
    goToPayoff('cumulative');
  };

  return (
    <div class={styles.pageContainer}>
      <StaleCacheBanner />
      {/* Toast for live contributions */}
      {latestAuthor && (
        <LiveToast
          message="New take just dropped"
          onDismiss={handleToastDismiss}
          stackIndex={nextToastSlot()}
        />
      )}

      {/* ── The payoff, announced where the guest actually is ──────────────────
          Each of these fires when a job the guest's take fed finally produced
          something watchable, and each carries the route to it. `goToPayoff`
          falls back to the thread rather than returning nothing: announcing a
          finished artifact with no way to reach it is the failure being fixed. */}
      {phaseRecapReady && (
        <LiveToast
          message={`${phaseRecapReady.phaseName} recap is ready.`}
          onDismiss={clearPhaseRecapReady}
          action={{
            label: 'Read it',
            onClick: () => goToPayoff(phaseRecapReady.phaseIndex),
          }}
          stackIndex={nextToastSlot()}
        />
      )}

      {/* The reel itself, when the event carried the token that reaches it. An
          older recap without one falls back to the written recap — still a real
          destination, so the label stops saying "watch" rather than sending
          someone to prose under a promise of playback. */}
      {recapRenderComplete && (
        <LiveToast
          message={
            recapRenderComplete.shareToken
              ? 'Your Highlight is ready to watch.'
              : 'Your Highlight is ready.'
          }
          onDismiss={clearRecapRenderComplete}
          action={
            recapRenderComplete.shareToken
              ? {
                  label: 'Watch',
                  onClick: () => navigate(`/r/${encodeURIComponent(recapRenderComplete.shareToken!)}`),
                }
              : { label: 'See it', onClick: () => goToPayoff('cumulative') }
          }
          stackIndex={nextToastSlot()}
        />
      )}

      {/* A terminal render failure is still news to the guest whose take fed it.
          Saying nothing here rebuilds the silence this block exists to end — the
          difference is only that there's nothing to route to. */}
      {recapRenderFailed && (
        <LiveToast
          message="We couldn't finish your Highlight. We'll keep trying."
          duration={5000}
          onDismiss={clearRecapRenderFailed}
          stackIndex={nextToastSlot()}
        />
      )}

      {deliverableReady && (
        deliverableReady.status === 'completed' ? (
          <LiveToast
            message="Your finished moment is ready."
            onDismiss={clearDeliverableReady}
            action={{ label: 'See it', onClick: () => goToPayoff('cumulative') }}
            stackIndex={nextToastSlot()}
          />
        ) : (
          // A failure has no payoff to route to, so it stays an ambient notice.
          <LiveToast
            message="We couldn't finish your moment."
            duration={5000}
            onDismiss={clearDeliverableReady}
            stackIndex={nextToastSlot()}
          />
        )
      )}

      {/* Advisory gap nudge (Plot Armor): an empty Proppian sphere is an open
          invitation, never a block. Backend-composed nudgeMessage is already in
          OurTake voice ("this story is still missing a Villain"). Lingers a
          little longer than a live-activity toast since it's an invitation to
          act, then quietly dismisses. */}
      {sphereBalance && (
        <LiveToast
          message={sphereBalance.nudgeMessage}
          duration={7000}
          onDismiss={clearSphereBalance}
          stackIndex={nextToastSlot()}
        />
      )}

      {/* Header with back nav */}
      <ChorusHeader back title={thread?.title ?? 'Moment'} onBack={() => navigate('/')} />

      {/* Story Hero Banner */}
      {thread && (
        <ThreadContextCard
          thread={thread}
          templateName={prompts?.context?.templateName}
          cumulativeSummary={briefForThisThread?.storySoFar ?? undefined}
        />
      )}

      {/* Catch Me Up — returning-contributor re-entry (renders only when there's
          new activity since the last visit; first-timers get the welcome card). */}
      {showCatchUp && briefForThisThread && (
        <CatchMeUpCard
          brief={briefForThisThread}
          onJumpIn={() => {
            acknowledgeBrief();
            document.getElementById('prompts-bar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          onDismiss={() => {
            acknowledgeBrief();
            setCatchUpDismissed(true);
          }}
        />
      )}

      {/* Welcome orientation for first-time visitors.
          Mutually exclusive with Catch Me Up, and gated on the brief having
          SETTLED — not merely on it being absent. The page-wide `loading` flag
          goes false before the brief request even starts, so checking the value
          alone would flash onboarding and then swap it for the catch-up card. */}
      {thread && threadId && briefStatus !== 'loading' && !hasCatchUpDelta && (
        <ThreadWelcomeCard
          threadId={threadId}
          threadTitle={thread.title}
          threadType={thread.threadType}
          threadDescription={thread.description}
          templateName={prompts?.context?.templateName}
          templateDescription={prompts?.context?.templateDescription}
          phaseName={prompts?.phaseName}
          phaseIndex={prompts?.phaseIndex}
          totalPhases={prompts?.totalPhases}
          creatorName={thread.creator?.displayName}
          contributionCount={thread.contributionCount}
          hasContributed={hasContributed}
          onDismiss={acknowledgeBrief}
        />
      )}

      {/* Phase intro video */}
      {thread?.currentPhase?.introVideoUrl && (
        <div class={styles.phaseIntroVideo}>
          <span class={styles.phaseIntroLabel}>Phase Introduction</span>
          <video
            class={styles.phaseIntroPlayer}
            src={thread.currentPhase.introVideoUrl}
            controls
            preload="metadata"
            playsInline
            poster={thread.currentPhase.introVideoThumbnailUrl ?? undefined}
          />
        </div>
      )}

      {/* Prompts / Themes (sticky) */}
      {prompts && (
        <div id="prompts-bar" class={styles.promptsBar}>
          <div class={styles.promptsHeader}>
            <span class={styles.phaseLabel}>
              Phase {prompts.phaseIndex + 1} &middot; {prompts.phaseName}
            </span>
            <span class={styles.progressBadge}>
              {prompts.context.phaseProgress.totalContributions}/{prompts.context.phaseProgress.minRequired}
            </span>
          </div>
          {promptsRefreshing ? (
            <AIWorkingIndicator compact label="Shaping prompts to your story…" />
          ) : (prompts.promptAdaptation?.isAdapted || promptsAdapted) ? (
            <span class={styles.shapedChip} title="These prompts were tailored to this story">
              Shaped by your story
            </span>
          ) : null}
          {prompts.phaseDescription && (
            <p class={styles.promptsDescription}>{prompts.phaseDescription}</p>
          )}
          {prompts.phaseGoalContext && (
            <div class={styles.phaseGoalContext}>
              <span class={styles.phaseGoalContextLabel}>{prompts.phaseGoalContext.label}</span>
              {prompts.phaseGoalContext.contextType === 'first_phase' && (
                <div class={styles.phaseGoalContextBody}>
                  {prompts.phaseGoalContext.threadTitle && (
                    <p class={styles.phaseGoalContextTitle}>
                      &ldquo;{prompts.phaseGoalContext.threadTitle}&rdquo;
                      {prompts.phaseGoalContext.templateDescription && (
                        <span class={styles.phaseGoalContextDesc}> &mdash; {prompts.phaseGoalContext.templateDescription}</span>
                      )}
                    </p>
                  )}
                  {prompts.phaseGoalContext.storyContext?.setting && (
                    <p class={styles.phaseGoalContextMeta}>📍 {prompts.phaseGoalContext.storyContext.setting}</p>
                  )}
                  {prompts.phaseGoalContext.storyContext?.keyPeople && prompts.phaseGoalContext.storyContext.keyPeople.length > 0 && (
                    <p class={styles.phaseGoalContextMeta}>👥 {prompts.phaseGoalContext.storyContext.keyPeople.join(', ')}</p>
                  )}
                  {prompts.phaseGoalContext.storyContext?.backstory && (
                    <p class={styles.phaseGoalContextBackstory}>{prompts.phaseGoalContext.storyContext.backstory}</p>
                  )}
                  {prompts.phaseGoalContext.storyContext?.contributorGuidance && (
                    <p class={styles.phaseGoalContextMeta}>💡 {prompts.phaseGoalContext.storyContext.contributorGuidance}</p>
                  )}
                </div>
              )}
              {prompts.phaseGoalContext.contextType === 'prior_phase' && (
                prompts.phaseGoalContext.voices.length > 0 ? (
                  <div class={styles.phaseGoalContextVoices}>
                    {prompts.phaseGoalContext.voices.map((voice, i) => (
                      <div key={i} class={styles.phaseGoalContextVoice}>
                        <span class={styles.phaseGoalContextAvatar}>{voice.displayName.charAt(0).toUpperCase()}</span>
                        <div>
                          <strong>{voice.displayName}</strong>
                          <span class={styles.phaseGoalContextSnippet}> {voice.snippet}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p class={styles.phaseGoalContextMeta}>No contributions from the previous phase yet.</p>
                )
              )}
            </div>
          )}
          {prompts.prompts?.length > 0 && (
            <div class={styles.promptsList}>
              {prompts.prompts.map((prompt, idx) => (
                <div key={idx} class={styles.promptItem}>
                  <span class={styles.promptNum}>{idx + 1}.</span>
                  <span>{prompt}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Live digest */}
      {prompts?.liveDigest && (
        <LiveDigestBar digest={prompts.liveDigest} />
      )}

      {/* Presence bar */}
      <PresenceBar entries={presence} />

      {/* Contribution feed */}
      <div class={styles.feed}>
        {allContributions.length === 0 && !loading && (
          <div class={styles.emptyFeed}>
            <div class={styles.emptyIcon}>
              <ChorusEmpty size={140} variant="aligned" />
            </div>
            <h3 class={styles.emptyHeading}>This moment is waiting for its first take.</h3>
            <p class={styles.emptyDescription}>
              {thread?.threadType === 'memory'
                ? 'A shared memory. What do you remember? A feeling, a detail, a photo — whatever comes to mind. Be the one who starts it.'
                : thread?.threadType === 'legacy'
                  ? "A life story told from many voices. Share a memory, an observation, or a moment that mattered to you."
                  : thread?.threadType === 'event'
                    ? "A shared event. What was it like from where you stood? Everyone experienced it differently — that's what makes it interesting."
                    : 'Add your perspective. What you saw, felt, or remember. A sentence is plenty.'}
            </p>
            <div class={styles.emptyArrow} aria-hidden="true">↓</div>
          </div>
        )}
        {allContributions.length > 0 && (
          <ThreadedContributionList
            contributions={allContributions}
            onBuildOn={handleBuildOn}
          />
        )}
        {nextCursor && (
          <button onClick={loadMore} class={styles.loadMoreButton}>
            Load More
          </button>
        )}
      </div>

      {/* Post-contribution banners */}
      {contributedThisSession && showInstallPrompt && !sheetOpen && (
        <div class={styles.bannerArea}>
          <InstallBanner onInstall={promptInstall} />
        </div>
      )}
      {contributedThisSession && (
        <div class={styles.bannerArea}>
          <PushOptIn />
        </div>
      )}

      {/* Offline-queue persistent-failure banner (template gate, etc.). */}
      {failedQueueCount > 0 && (
        <div class={styles.queueFailureBanner} role="alert">
          {failedQueueCount === 1
            ? '1 saved take needs your attention to post — open it from your queue.'
            : `${failedQueueCount} saved takes need your attention to post.`}
        </div>
      )}

      {/* Hot Seat / Campfire Circle blocking message — surfaced above the FAB
          so users see why posting is disabled before tapping. */}
      {hotSeatBlocked && (
        <div id="hot-seat-explanation">
          <TurnOrderBadge currentHolderName={null} />
        </div>
      )}

      {/* Floating action button. When Hot Seat blocks posting, use
          aria-disabled (not the disabled attribute) and aria-describedby
          so screen readers announce *why* the action is inert instead of
          just skipping over a dimmed button. */}
      <button
        class={styles.fab}
        aria-disabled={hotSeatBlocked || undefined}
        aria-describedby={hotSeatBlocked ? 'hot-seat-explanation' : undefined}
        onClick={() => {
          if (hotSeatBlocked) return;
          setSheetOpen(true);
        }}
      >
        {allContributions.length > 0 && <IconPlus size={16} />}
        {allContributions.length === 0
          ? 'Add the First Take'
          : hasContributed
            ? 'Add Another Take'
            : 'Add Your Take'}
      </button>

      {/* Upload progress */}
      {uploadProgress !== null && (
        <div class={styles.uploadProgress}>
          <div class={styles.uploadProgressBar} style={{ width: `${uploadProgress * 100}%` }} />
          <span class={styles.uploadProgressText}>Uploading... {Math.round(uploadProgress * 100)}%</span>
        </div>
      )}

      {/* Upload error with retry */}
      {uploadError && (
        <div class={styles.uploadError}>
          <p>Upload failed: {uploadError}</p>
          <div class={styles.uploadErrorActions}>
            <button
              class={styles.retryButton}
              onClick={() => { void handleRetryUpload(); }}
              disabled={submittingGlobal || !failedSubmission || !navigator.onLine}
            >
              Retry upload
            </button>
            <button
              class={styles.retryButton}
              onClick={() => {
                setUploadError(null);
                setFailedSubmission(null);
              }}
              disabled={submittingGlobal}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Bottom sheet */}
      <ContributeSheet
        open={sheetOpen}
        onClose={handleSheetClose}
        onUnifiedSubmit={handleUnifiedSubmit}
        submitting={submittingGlobal}
        threadId={threadId}
        prompts={prompts?.prompts}
        promptExamples={prompts?.promptExamples}
        promptStates={promptFlowStates}
        draftsPostable={!promptLifecycleLocked && !prompts?.completed && (
          prompts?.context.phaseProgress.maxAllowed == null ||
          Math.max(
            prompts.context.phaseProgress.myContributions,
            optimisticMyContributionCount ?? 0,
          ) + queuedPromptIndices.size < prompts.context.phaseProgress.maxAllowed
        )}
        closeBlocked={rolePickerOpen || arcPickerOpen}
        allowedTypes={prompts?.requiredContributionTypes}
        buildOnTarget={buildOnTarget}
        onClearBuildOn={() => setBuildOnTarget(null)}
        threadType={thread?.threadType ?? undefined}
        storyContext={thread?.storyContext}
        liveDigest={prompts?.liveDigest}
        phaseName={prompts?.phaseName}
        phaseDescription={prompts?.phaseDescription}
        momentTitle={thread?.title}
        onViewChorus={() => navigate(`/thread/${threadId}`)}
        onViewPayoff={goToHighlight}
      />

      {/* Template-gate pickers (Plot Armor role / Mood Ring arc shape) */}
      <RolePickerSheet
        open={rolePickerOpen}
        roleOccupancy={roleOccupancy}
        roleLexicon={thread?.templateState?.roleLexicon}
        mode={rolePickerMode}
        errorMessage={rolePickerError}
        onSelect={handleRoleSelected}
        onDismiss={handleRolePickerDismiss}
      />
      <ArcPickerSheet
        open={arcPickerOpen}
        mode={arcPickerMode}
        errorMessage={arcPickerError}
        onSelect={handleArcSelected}
        onDismiss={handleArcPickerDismiss}
      />
    </div>
  );
}
